import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
import { Plus, Trash2, ChevronRight, ChevronDown, Pencil, Check, X } from 'lucide-react'

// Recursively build tree from flat nodes list
const buildTree = (nodes, parentId = null) =>
  nodes
    .filter(n => n.parent_id === parentId)
    .sort((a, b) => a.rank_order - b.rank_order)
    .map(n => ({ ...n, children: buildTree(nodes, n.id) }))

const NODE_TYPES = ['role', 'unit', 'element']
const TYPE_COLOR = {
  role: 'bg-amber-900 text-amber-300',
  unit: 'bg-blue-900 text-blue-300',
  element: 'bg-green-900 text-green-300',
}

export default function MCSBuilder({ orgId, userId }) {
  const [templates, setTemplates] = useState([])
  const [activeTemplate, setActiveTemplate] = useState(null)
  const [nodes, setNodes] = useState([])
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')

  // New template
  const [newTemplateName, setNewTemplateName] = useState('')
  const [creatingTemplate, setCreatingTemplate] = useState(false)
  const [showNewTemplate, setShowNewTemplate] = useState(false)

  // Node editing — SEC 1 default flipped to 'unit' (the execution
  // layer). Role nodes are now the explicit chain-of-command pick
  // rather than the implicit default.
  const [editingNode, setEditingNode] = useState(null)
  const [editName, setEditName] = useState('')
  const [editType, setEditType] = useState('unit')
  const [addingChildOf, setAddingChildOf] = useState(null)
  const [newNodeName, setNewNodeName] = useState('')
  const [newNodeType, setNewNodeType] = useState('unit')
  const [expanded, setExpanded] = useState({})

  useEffect(() => { fetchTemplates() }, [orgId])
  useEffect(() => { if (activeTemplate) fetchNodes(activeTemplate.id) }, [activeTemplate])

  const fetchTemplates = async () => {
    const { data } = await supabase
      .from('mcs_templates')
      .select('*')
      .eq('org_id', orgId)
      .order('created_at', { ascending: false })
    setTemplates(data || [])
  }

  const fetchNodes = async (templateId) => {
    setLoading(true)
    const { data } = await supabase
      .from('mcs_template_nodes')
      .select('*')
      .eq('template_id', templateId)
      .order('rank_order')
    setNodes(data || [])
    setLoading(false)
  }

  const createTemplate = async () => {
    if (!newTemplateName.trim()) return
    setCreatingTemplate(true)
    const { data, error } = await supabase
      .from('mcs_templates')
      .insert({ org_id: orgId, name: newTemplateName.trim(), created_by: userId })
      .select().limit(1)
    if (error) { setMessage('Failed: ' + error.message) }
    else {
      setNewTemplateName('')
      setShowNewTemplate(false)
      await fetchTemplates()
      setActiveTemplate(data[0])
    }
    setCreatingTemplate(false)
  }

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template and all its nodes?')) return
    await supabase.from('mcs_template_nodes').delete().eq('template_id', id)
    await supabase.from('mcs_templates').delete().eq('id', id)
    if (activeTemplate?.id === id) setActiveTemplate(null)
    setNodes([])
    fetchTemplates()
  }

  const addNode = async (parentId = null) => {
    if (!newNodeName.trim() || !activeTemplate) return
    const siblings = nodes.filter(n => n.parent_id === parentId)
    const { error } = await supabase.from('mcs_template_nodes').insert({
      template_id: activeTemplate.id,
      name: newNodeName.trim(),
      node_type: newNodeType,
      parent_id: parentId,
      rank_order: siblings.length,
    })
    if (error) setMessage('Failed: ' + error.message)
    else {
      setNewNodeName('')
      setAddingChildOf(null)
      if (parentId) setExpanded(e => ({ ...e, [parentId]: true }))
      fetchNodes(activeTemplate.id)
    }
  }

  const saveNodeEdit = async (nodeId) => {
    await supabase.from('mcs_template_nodes').update({
      name: editName.trim(),
      node_type: editType,
    }).eq('id', nodeId)
    setEditingNode(null)
    fetchNodes(activeTemplate.id)
  }

  const deleteNode = async (nodeId) => {
    const collectIds = (id) => {
      const children = nodes.filter(n => n.parent_id === id).map(n => n.id)
      return [id, ...children.flatMap(collectIds)]
    }
    const ids = collectIds(nodeId)
    await supabase.from('mcs_template_nodes').delete().in('id', ids)
    fetchNodes(activeTemplate.id)
  }

  const toggleExpand = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }))

  const renderNode = (node, depth = 0) => {
    const hasChildren = node.children?.length > 0
    // Default expanded — same as EventMCS — so the full structure is visible
    // when the tree first renders. User can collapse explicitly per node.
    const isExpanded = expanded[node.id] !== false
    const isEditing = editingNode === node.id
    const isAddingChild = addingChildOf === node.id

    return (
      <div key={node.id}>
        <div
          className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-gray-700 group"
          style={{ paddingLeft: `${8 + depth * 20}px` }}
        >
          <button onClick={() => toggleExpand(node.id)} className="w-4 flex-shrink-0 text-gray-500">
            {hasChildren
              ? (isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />)
              : <span className="w-3 inline-block" />
            }
          </button>

          {isEditing ? (
            <div className="flex items-center gap-2 flex-1">
              <input
                className="bg-gray-600 text-white rounded px-2 py-0.5 text-sm flex-1 focus:outline-none"
                value={editName}
                onChange={e => setEditName(e.target.value)}
                maxLength={48}
                autoFocus
              />
              <select
                className="bg-gray-600 text-white rounded px-2 py-0.5 text-xs focus:outline-none"
                value={editType}
                onChange={e => setEditType(e.target.value)}
              >
                {NODE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <button onClick={() => saveNodeEdit(node.id)} className="text-green-400 hover:text-green-300"><Check size={14} /></button>
              <button onClick={() => setEditingNode(null)} className="text-gray-400 hover:text-white"><X size={14} /></button>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-1">
              <span className="text-sm text-gray-200">{node.name}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded ${TYPE_COLOR[node.node_type] || 'bg-gray-700 text-gray-400'}`}>
                {node.node_type}
              </span>
              <div className="hidden group-hover:flex items-center gap-1 ml-auto">
                <button
                  onClick={() => { setAddingChildOf(node.id); setExpanded(e => ({ ...e, [node.id]: true })) }}
                  className="text-xs text-gray-400 hover:text-amber-400 flex items-center gap-0.5"
                  title="Add child"
                >
                  <Plus size={11} />
                </button>
                <button
                  onClick={() => { setEditingNode(node.id); setEditName(node.name); setEditType(node.node_type || 'unit') }}
                  className="text-gray-400 hover:text-white"
                  title="Rename"
                >
                  <Pencil size={11} />
                </button>
                <button
                  onClick={() => deleteNode(node.id)}
                  className="text-gray-400 hover:text-red-400"
                  title="Delete"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            </div>
          )}
        </div>

        {isAddingChild && (
          // Mobile (<sm) stacks the form vertically — the row was pushing the
          // ✓ Save button off-screen at narrow widths once the depth indent
          // ate the remaining space.
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-1 px-2" style={{ paddingLeft: `${8 + (depth + 1) * 20}px` }}>
            <input
              className="bg-gray-700 text-white rounded px-2 py-0.5 text-sm w-full sm:flex-1 focus:outline-none focus:ring-1 focus:ring-amber-500"
              placeholder="Node name"
              value={newNodeName}
              onChange={e => setNewNodeName(e.target.value)}
              maxLength={48}
              autoFocus
              onKeyDown={e => e.key === 'Enter' && addNode(node.id)}
            />
            <div className="flex items-center gap-2">
              <select
                className="bg-gray-700 text-white rounded px-2 py-1 sm:py-0.5 text-xs flex-1 sm:flex-none focus:outline-none"
                value={newNodeType}
                onChange={e => setNewNodeType(e.target.value)}
              >
                {NODE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <button onClick={() => addNode(node.id)} className="text-green-400 hover:text-green-300 p-1" title="Save"><Check size={16} /></button>
              <button onClick={() => setAddingChildOf(null)} className="text-gray-400 hover:text-white p-1" title="Cancel"><X size={16} /></button>
            </div>
          </div>
        )}

        {hasChildren && isExpanded && node.children.map(child => renderNode(child, depth + 1))}
      </div>
    )
  }

  const tree = buildTree(nodes)

  // Vertical layout: templates stacked on top, the active template's tree
  // editor below. Mirrors the EventMCS indented tree style for consistency
  // and mobile friendliness.
  return (
    <div className="flex flex-col gap-6 mt-4">
      {/* Template list */}
      <div className="w-full">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Templates</p>
          <button
            onClick={() => setShowNewTemplate(!showNewTemplate)}
            className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1"
          >
            <Plus size={12} /> New
          </button>
        </div>

        {showNewTemplate && (
          <div className="mb-3 flex gap-2">
            <input
              className="flex-1 bg-gray-700 text-white rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-amber-500"
              placeholder="Template name"
              value={newTemplateName}
              onChange={e => setNewTemplateName(e.target.value)}
              maxLength={48}
              autoFocus
              onKeyDown={e => e.key === 'Enter' && createTemplate()}
            />
            <button onClick={createTemplate} disabled={creatingTemplate} className="text-green-400 hover:text-green-300">
              <Check size={16} />
            </button>
            <button onClick={() => setShowNewTemplate(false)} className="text-gray-400 hover:text-white">
              <X size={16} />
            </button>
          </div>
        )}

        {templates.length === 0 ? (
          <p className="text-gray-600 text-xs">No templates yet</p>
        ) : (
          <div className="space-y-1">
            {templates.map(t => (
              <div
                key={t.id}
                className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer group ${
                  activeTemplate?.id === t.id ? 'bg-amber-900 text-amber-300' : 'bg-gray-800 text-gray-300 hover:bs-gray-700'
                }`}
                onClick={() => setActiveTemplate(t)}
              >
                <span className="text-sm truncate flex-1">{t.name}</span>
                <button
                  onClick={e => { e.stopPropagation(); deleteTemplate(t.id) }}
                  className="hidden group-hover:block text-gray-500 hover:text-red-400 ml-2"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Node editor */}
      <div className="w-full">
        {!activeTemplate ? (
          <div className="text-center py-12 text-gray-600">
            <p className="text-sm">Select a template to edit its command structure</p>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="font-medium text-gray-200">{activeTemplate.name}</p>
                <p className="text-xs text-gray-500 mt-0.5">{nodes.length} nodes</p>
              </div>
              <button
                onClick={() => { setAddingChildOf('root'); setNewNodeName('') }}
                className="flex items-center gap-1.5 text-xs bg-amber-500 hover:bs-amber-400 text-gray-900 font-bold px-3 py-1.5 rounded-lg"
              >
                <Plus size={12} /> Add Root Node
              </button>
            </div>

            {addingChildOf === 'root' && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3 px-2">
                <input
                  className="bg-gray-700 text-white rounded px-2 py-1 text-sm w-full sm:flex-1 focus:outline-none focus:ring-1 focus:ring-amber-500"
                  placeholder="Node name (e.g. Faction Commander)"
                  value={newNodeName}
                  onChange={e => setNewNodeName(e.target.value)}
                  maxLength={48}
                  autoFocus
                  onKeyDown={e => e.key === 'Enter' && addNode(null)}
                />
                <div className="flex items-center gap-2">
                  <select
                    className="bg-gray-700 text-white rounded px-2 py-1 text-xs flex-1 sm:flex-none focus:outline-none"
                    value={newNodeType}
                    onChange={e => setNewNodeType(e.target.value)}
                  >
                    {NODE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <button onClick={() => addNode(null)} className="text-green-400 hover:text-green-300 p-1" title="Save"><Check size={18} /></button>
                  <button onClick={() => setAddingChildOf(null)} className="text-gray-400 hover:text-white p-1" title="Cancel"><X size={18} /></button>
                </div>
              </div>
            )}

            {message && (
              <p className="text-red-400 text-xs mb-2">{message}</p>
            )}

            {loading ? (
              <p className="text-gray-500 text-sm">Loading...</p>
            ) : tree.length === 0 ? (
              <p className="text-gray-600 text-sm px-2">No nodes yet. Add a root node to start building the command structure.</p>
            ) : (
              <div className="bg-gray-800 rounded-xl border border-gray-700 py-2">
                {tree.map(node => renderNode(node, 0))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
